/*
 * Copyright (c) 2022. Nedaware SL
 */

import {FormManager} from "./edition-row/edition-row.component";
import {AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from "@angular/forms";
import {InternalPropertyMap} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {TypeStr} from "../../basic-entity-back/property-type/type-str";
import {BasicEntityInputComponent} from "../basic-entity-input/basic-entity-input.component";
import {EntityManager} from "../../basic-entity-back/entity-manager/entity-manager";
import {Resource} from "../../api/resource";
import {InterfaceProviderService} from "../../basic-entity-back/services/interface-provider.service";
import {ReadWrite} from "../../basic-entity-back/basic-entity-interface/mapping-external";

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */

export class EditionDialogFormManager<T extends Resource> implements FormManager {
    /** FormGroup which controls all the dialog values */
    private _formGroup: UntypedFormGroup;
    public get formGroup() {
        return this._formGroup;
    }

    private _properties: InternalPropertyMap[];
    private _disableAll = false;

    constructor(public readonly allFieldsDisabled: boolean,
                public readonly allowIdEdition: boolean,
                private _fb: UntypedFormBuilder,
                private _manager: EntityManager<T>,
                private _interfaceProvider: InterfaceProviderService) {
    }

    get disableAll(): any {
        return this._disableAll;
    }

    set disableAll(value: any) {
        if (typeof value === 'boolean') {
            if (this._disableAll !== value) {
                if (value) {
                    this._formGroup.disable({emitEvent: false});
                } else {
                    this._formGroup.enable({emitEvent: false});
                }
                this._disableAll = value;
            }
        } else if (Array.isArray(value)) {
            console.log(this.formGroup);
            this.formGroup.get(value[0]).enable();
        }
    }

    /**
     * Clears both the form group and the properties and establishes disableAll as false
     */
    public reset() {
        this._formGroup = null;
        this._properties = null;
        this._disableAll = false;
    }

    /**
     * Checks whether a property should be disabled or not
     * @param prop - The property
     */
    public shouldPropBeDisabled(prop: InternalPropertyMap) {
        return this.allFieldsDisabled || this._disableAll || (
            (prop.isId && !this.allowIdEdition) ||
            prop.modelKey === "iri" ||
            prop.modelKey === "entityType" ||
            prop.readWrite === ReadWrite.ReadOnly
        );
    }

    /**
     * Creates the FormGroup to manage the values. This action can only be performed
     * once, since for then on the manager will be associated to the returned formGroup.
     * @private
     */
    public createFormGroup(model: T, properties: InternalPropertyMap[]): UntypedFormGroup {
        if (this._formGroup) {
            throw new Error('Error: formGroup already created');
        }

        const config = {};
        for (const prop of properties) {
            config[prop.modelKey] = this.createControl(prop, this._propValue(prop, model));
        }
        this._formGroup = this._fb.group(config);
        this._properties = properties;
        return this._formGroup;
    }

    /**
     * Creates the right control for the property given. When working with array properties
     * the index param will indicate the index of the control, while a value of null indicates
     * to generate the whole array.
     * @param property
     * @param val
     * @param index
     */
    public createControl(property: InternalPropertyMap, val: any, index: number | null = null): AbstractControl {
        let control: AbstractControl;
        if (index === null && property.array) {
            control = this._fb.array(this._createFormArrayControls(val, property));
            if (this.shouldPropBeDisabled(property)) {
                control.disable();
            }
        } else {
            control = BasicEntityInputComponent.sFormControlForColumn(
                this._fb,
                property,
                this._safeValue(val, property),
                this.shouldPropBeDisabled(property),
                index,
                index === null ? null : true
            );
        }
        return control;
    }

    /**
     * Gets a safe value from the model to assign to the FormGroup, have in account
     * that nested models need to be cloned, for example.
     * If the property is an array, the original value will be returned,
     * since setting the value will perform cloning on each element in
     * createControl.
     * @see createControl
     * @param val - the unsafe value
     * @param property - the property whose value we want to obtain
     */
    private _safeValue(val: any, property: InternalPropertyMap): any {
        if (property.type.toString() === TypeStr.NestedModel && !property.array) {
            return (
                val instanceof Array ?
                    val.map(v => this._safeValue(v, property)) :
                    this._interfaceProvider
                        .interfaceForModel(property.type.asNestedModel().modelType)
                        .serialiser.clone(val)
            );
        } else {
            return val;
        }
    }

    /**
     * Get the value for any property correctly (arrays included)
     * @param property - The property to get the value for
     */
    public getValue(property: InternalPropertyMap): any {
        let val = this.formGroup.get(property.modelKey).value;
        if (property.array) {
            val = val.map(v => EditionDialogFormManager._sGetValueNullControl(v[BasicEntityInputComponent.UNIQUE_CONTROL_NAME], property));
        } else {
            val = EditionDialogFormManager._sGetValueNullControl(val, property);
        }
        return val;
    }

    private static _sGetValueNullControl(value: any, property: InternalPropertyMap) {
        if (value === "" && property.nullable) {
            return null;
        }
        return value;
    }

    /**
     * Sets the value for any property (arrays included)
     * @param property - the property to set the value for
     * @param value - the value to set
     * @param emitEvent - the emitEvent of the options
     */
    public setValue(property: InternalPropertyMap, value: any, emitEvent: boolean = true) {
        const safeVal = this._safeValue(value, property);
        const control: AbstractControl = this.formGroup.get(property.modelKey);
        if (!control) {
            console.error('Control not present in the form: ', property);
            throw new Error('The control for the property is not present in the form.');
        }
        if (property.array) {
            const formArray = control as UntypedFormArray;
            formArray.controls.splice(0, formArray.controls.length,
                ...this._createFormArrayControls(value, property));
            formArray.updateValueAndValidity({emitEvent: emitEvent});
        } else {
            control.setValue(safeVal, {emitEvent: emitEvent});
        }
    }

    /**
     * Takes the form values and assigns them to the model.
     * Notice that createFormGroup first.
     * @see createFormGroup
     * @param model - the model to assign the values to
     */
    public formToModel(model: T) {
        for (const prop of this._properties) {
            if (this.shouldPropBeDisabled(prop)) {
                continue;
            }
            const v = this.getValue(prop);
            if (v !== undefined) {
                this._manager.loader.entityInterface.serialiser.setValue(model, prop.modelKey, v);
            }
        }
    }

    /**
     * Takes all the values from the model and assigns them to the formGroup.
     * Notice that createFormGroup first.
     * @see createFormGroup
     * @param model - the model to get the values from
     */
    public modelToForm(model: T) {
        for (const prop of this._properties) {
            this.setValue(prop, this._propValue(prop, model));
        }
    }

    /**
     * Creates an array of controls for a FormArray with the given values for the given property
     * @param val - the array of values, null will be interpreted as an empty array (we never assign null)
     * @param prop - property (of array type) to create the array of controls for
     */
    private _createFormArrayControls(val: any[] | null, prop: InternalPropertyMap): AbstractControl[] {
        if (!val) {
            val = [];
        }
        return val.map((value, idx) => this.createControl(prop, value, idx));
    }

    /**
     * Gets the value in the model for the property corresponding the
     * given column.
     * @param col - The column to find the value for
     * @param model - The model to get the value from
     */
    private _propValue(col: InternalPropertyMap, model: T): any {
        return this._manager.loader.entityInterface.serialiser.getValue(model, col.modelKey);
    }
}
